import { Component, Inject, OnInit, signal, inject } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatNativeDateModule, MAT_DATE_LOCALE, DateAdapter, MAT_DATE_FORMATS, NativeDateAdapter, MAT_NATIVE_DATE_FORMATS } from '@angular/material/core';
import { LucideAngularModule, ChevronDown, ChevronUp, Search, X, Calendar, SlidersHorizontal } from "lucide-angular";
import { PicklistType, PicklistItem } from '../../models/picklist.model';

export interface FilterModalComponentData {
  picklists: {
    types: PicklistType[];
    items: { [typeId: number]: PicklistItem[] };
  };
  activeFilters?: { [key: string]: number[] | string }; // Filtros aplicados actualmente (arrays para picklists, strings para otros)
}

export interface PicklistFilterState {
  typeId: number;
  typeName: string;
  originalName: string; // Nombre original del backend (categoria_1, tipo, etc.)
  expanded: boolean;
  searchQuery: string;
  selectedItemIds: number[];
  allItems: PicklistItem[];
}

@Component({
  selector: 'app-filter-modal',
  standalone: true,
  imports: [
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    MatDividerModule,
    MatDatepickerModule,
    MatNativeDateModule,
    LucideAngularModule,
    MatSnackBarModule
],
  providers: [
    { provide: DateAdapter, useClass: NativeDateAdapter },
    { provide: MAT_DATE_LOCALE, useValue: 'es-CL' },
    { provide: MAT_DATE_FORMATS, useValue: MAT_NATIVE_DATE_FORMATS }
  ],
  templateUrl: './filter-modal.component.html',
  styleUrls: ['./filter-modal.component.scss']
})
export class FilterModalComponent implements OnInit {
  // Icons
  readonly ChevronDown = ChevronDown;
  readonly ChevronUp = ChevronUp;
  readonly Search = Search;
  readonly X = X;
  readonly Calendar = Calendar;
  readonly SlidersHorizontal = SlidersHorizontal;

  readonly snackBar = inject(MatSnackBar);

  // state of filters for wach type of picklist
  filterStates = signal<PicklistFilterState[]>([]);

  // New filter fields
  modifiedDateFrom: Date | null = null;
  modifiedDateTo: Date | null = null;
  identifier = '';
  content = '';
  keywords = '';
  protocol = '';

  // Text search section expansion state
  textSearchExpanded = false;

  // Date filter section expansion state
  dateFilterExpanded = false;

  constructor(
    public dialogRef: MatDialogRef<FilterModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: FilterModalComponentData
  ) {}

  ngOnInit(): void {
    this.initializeFilterStates();
    this.restoreActiveFilters();
  }

  /**
   * Restaurar filtros activos que fueron previamente aplicados
   */
  private restoreActiveFilters(): void {
    if (!this.data.activeFilters) return;

    this.restorePicklistFilters();
    this.restoreDateFilters();
    this.restoreTextFilters();
    this.autoExpandSections();
  }

  /**
   * Restaurar filtros de picklists
   */
  private restorePicklistFilters(): void {
    this.filterStates.update(states =>
      states.map(state => {
        const itemIds = this.data.activeFilters?.[state.originalName];
        if (itemIds && Array.isArray(itemIds) && itemIds.length > 0) {
          return { ...state, selectedItemIds: [...itemIds] };
        }
        return state;
      })
    );
  }

  /**
   * Restaurar filtros de fechas
   */
  private restoreDateFilters(): void {
    this.modifiedDateFrom = this.restoreDateField('modified_date_from');
    this.modifiedDateTo = this.restoreDateField('modified_date_to');
  }

  /**
   * Restaurar un campo de fecha específico
   */
  private restoreDateField(fieldName: string): Date | null {
    const value = this.data.activeFilters?.[fieldName];
    return (value && typeof value === 'string') ? this.parseDateFromISO(value) : null;
  }

  /**
   * Restaurar filtros de texto
   */
  private restoreTextFilters(): void {
    this.identifier = this.restoreTextField('identifier');
    this.content = this.restoreTextField('content');
    this.keywords = this.restoreTextField('keywords');
    this.protocol = this.restoreTextField('protocol');
  }

  /**
   * Restaurar un campo de texto específico
   */
  private restoreTextField(fieldName: string): string {
    const value = this.data.activeFilters?.[fieldName];
    return (value && typeof value === 'string') ? value : '';
  }

  /**
   * Auto-expandir secciones si tienen filtros activos
   */
  private autoExpandSections(): void {
    if (this.hasActiveTextFilters()) {
      this.textSearchExpanded = true;
    }
    
    if (this.modifiedDateFrom || this.modifiedDateTo) {
      this.dateFilterExpanded = true;
    }
  }

  /**
   * Verificar si hay filtros de texto activos
   */
  private hasActiveTextFilters(): boolean {
    return !!(this.identifier || this.content || this.keywords || this.protocol);
  }

  /**
   * adjust name format from picklist to display
   */
  private formatPicklistTypeName(name: string): string {
    // Caso especial para categoria_2
    if (name === 'categoria_2') {
      return 'Categoría 2';
    }
    // replace underscore with spaces and capitalize first letter
    return name
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  private initializeFilterStates(): void {
    const states: PicklistFilterState[] = this.data.picklists.types.map(type => ({
      typeId: type.picklistTypeId,
      typeName: this.formatPicklistTypeName(type.name),
      originalName: type.name, // Guardar nombre original del backend
      expanded: false,
      searchQuery: '',
      selectedItemIds: [],
      allItems: this.data.picklists.items[type.picklistTypeId] || []
    }));
    this.filterStates.set(states);
  }

  /**
   * Toggle expansion of picklist type
   */
  toggleExpansion(typeId: number): void {
    this.filterStates.update(states =>
      states.map(state =>
        state.typeId === typeId
          ? { ...state, expanded: !state.expanded }
          : state
      )
    );
  }

  /**
   * Update search query
   */
  updateSearchQuery(typeId: number, query: string): void {
    this.filterStates.update(states =>
      states.map(state =>
        state.typeId === typeId
          ? { ...state, searchQuery: query }
          : state
      )
    );
  }

  /**
   * Clear search query for picklist type (X button)
   */
  clearSearch(typeId: number): void {
    this.updateSearchQuery(typeId, '');
  }

  /**
   * Toggle item selection
   */
  toggleItemSelection(typeId: number, itemId: number): void {
    this.filterStates.update(states =>
      states.map(state => {
        if (state.typeId === typeId) {
          const selectedIds = [...state.selectedItemIds];
          const index = selectedIds.indexOf(itemId);
          if (index > -1) {
            selectedIds.splice(index, 1);
          } else {
            selectedIds.push(itemId);
          }
          return { ...state, selectedItemIds: selectedIds };
        }
        return state;
      })
    );
  }

  /**
   * Check if an item is selected
   */
  isItemSelected(typeId: number, itemId: number): boolean {
    const state = this.filterStates().find(s => s.typeId === typeId);
    return state ? state.selectedItemIds.includes(itemId) : false;
  }

  /**
   * Obtain filtered items from picklist type based on search query
   */
  getFilteredItems(state: PicklistFilterState): PicklistItem[] {
    if (!state.searchQuery.trim()) {
      return state.allItems;
    }
    const query = state.searchQuery.toLowerCase();
    return state.allItems.filter(item =>
      item.name.toLowerCase().includes(query) ||
      item.code.toLowerCase().includes(query)
    );
  }

  getSelectedCount(state: PicklistFilterState): number {
    return state.selectedItemIds.length;
  }

  /**
   * Get count of active text search filters
   */
  getTextSearchCount(): number {
    let count = 0;
    if (this.identifier.trim()) count++;
    if (this.content.trim()) count++;
    if (this.keywords.trim()) count++;
    if (this.protocol.trim()) count++;
    return count;
  }

  /**
   * Toggle text search section expansion
   */
  toggleTextSearchExpansion(): void {
    this.textSearchExpanded = !this.textSearchExpanded;
  }

  /**
   * Toggle date filter section expansion
   */
  toggleDateFilterExpansion(): void {
    this.dateFilterExpanded = !this.dateFilterExpanded;
  }

  /**
   * Get count of active date filters
   */
  getDateFilterCount(): number {
    let count = 0;
    if (this.modifiedDateFrom) count++;
    if (this.modifiedDateTo) count++;
    return count;
  }

  hasActiveFilters(): boolean {
    const hasPicklistFilters = this.filterStates().some(state => state.selectedItemIds.length > 0);
    const hasDateFilters = this.modifiedDateFrom !== null || this.modifiedDateTo !== null;
    const hasTextFilters = this.identifier.trim() !== '' || this.content.trim() !== '' || 
                          this.keywords.trim() !== '' || this.protocol.trim() !== '';
    return hasPicklistFilters || hasDateFilters || hasTextFilters;
  }

  /**
   * Clear all filters (botón limpiar)
   */
  clearAllFilters(): void {
    this.filterStates.update(states =>
      states.map(state => ({
        ...state,
        selectedItemIds: [],
        searchQuery: '',
        expanded: false
      }))
    );
    
    // Clear new filters
    this.modifiedDateFrom = null;
    this.modifiedDateTo = null;
    this.identifier = '';
    this.content = '';
    this.keywords = '';
    this.protocol = '';
    
    // Reset expansion states
    this.textSearchExpanded = false;
    this.dateFilterExpanded = false;
  }

  private showError(message: string): void {
    this.snackBar.open(message, 'Cerrar', {
      duration: 5000,
      horizontalPosition: 'end',
      verticalPosition: 'top',
      panelClass: ['error-snackbar']
    });
  }

  /**
   * Apply filters and close modal
   */
  applyFilters(): void {
    // Validate date range
    if (this.modifiedDateFrom && this.modifiedDateTo && this.modifiedDateFrom > this.modifiedDateTo) {
      this.showError('La fecha "Desde" no puede ser mayor que la fecha "Hasta"');
      return;
    }

    const filters: { [key: string]: any } = {};

    // Add picklist filters
    this.filterStates().forEach(state => {
      if (state.selectedItemIds.length > 0) {
        // Usar el nombre original del backend directamente como parámetro API
        filters[state.originalName] = state.selectedItemIds;
      }
    });

    // Add date filters (ISO 8601 format with time)
    if (this.modifiedDateFrom) {
      filters['modified_date_from'] = this.formatDateToISO(this.modifiedDateFrom, false);
    }
    if (this.modifiedDateTo) {
      filters['modified_date_to'] = this.formatDateToISO(this.modifiedDateTo, true);
    }

    // Add text filters
    if (this.identifier.trim()) {
      filters['identifier'] = this.identifier.trim();
    }
    if (this.content.trim()) {
      filters['content'] = this.content.trim();
    }
    if (this.keywords.trim()) {
      filters['keywords'] = this.keywords.trim();
    }
    if (this.protocol.trim()) {
      filters['protocol'] = this.protocol.trim();
    }

    this.dialogRef.close(filters);
  }

  /**
   * Format date to ISO 8601 with time (YYYY-MM-DDTHH:MM:SS)
   * For "from" dates, use 00:00:00
   * For "to" dates, use 23:59:59
   */
  private formatDateToISO(date: Date, isEndDate: boolean = false): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const time = isEndDate ? '23:59:59' : '00:00:00';
    return `${year}-${month}-${day}T${time}`;
  }

  /**
   * Parse ISO date string to Date object in local timezone
   * Avoids timezone offset issues by extracting date components
   */
  private parseDateFromISO(isoString: string): Date {
    // Extract date part (YYYY-MM-DD) from ISO string like "2026-02-12T00:00:00"
    const datePart = isoString.split('T')[0];
    const [year, month, day] = datePart.split('-').map(Number);
    // Create Date in local timezone (month is 0-indexed)
    return new Date(year, month - 1, day);
  }

  /**
   * Clear all filters and close modal notifying parent to reload data
   */
  clearAndReload(): void {
    this.clearAllFilters();
    // Close modal with special indicator to clear global filters
    this.dialogRef.close({ clearFilters: true });
  }

  /**
   * TrackBy to optimize rendering of picklist types
   */
  trackByTypeId(index: number, state: PicklistFilterState): number {
    return state.typeId;
  }

  /**
   * TrackBy to optimize rendering of picklist types
   */
  trackByItemId(index: number, item: any): number {
    return item.picklistItemId;
  }
}